import * as React from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { ActionBucket, RenewalFamily } from "@/types/renewals";

import { BOARD_COLUMNS, BOARD_TILES, ACTION_BUCKET_LABELS } from "../constants";
import { compactGbp } from "../renewal-format";
import { RenewalCard } from "./renewal-card";

interface Props {
  // Already filtered and sorted by the page; each column keeps that order.
  families: RenewalFamily[];
  ownerLabel: (ownerUserId: string | null) => string | null;
  onViewInTable: (bucket: ActionBucket) => void;
}

interface Group {
  families: RenewalFamily[];
  totalValue: string;
}

const groupByBucket = (families: RenewalFamily[]) => {
  const groups = new Map<ActionBucket, RenewalFamily[]>();
  families.forEach(family => {
    const members = groups.get(family.action_bucket);
    if (members) members.push(family);
    else groups.set(family.action_bucket, [family]);
  });
  return (bucket: ActionBucket): Group => {
    const members = groups.get(bucket) ?? [];
    const total = members.reduce((sum, family) => sum + Number(family.total_contract_value_gbp ?? 0), 0);
    return { families: members, totalValue: compactGbp(total) ?? "£0" };
  };
};

export const RenewalBoard = ({ families, ownerLabel, onViewInTable }: Props) => {
  const group = React.useMemo(() => groupByBucket(families), [families]);

  return (
    // The board scrolls both ways inside the height the page leaves it. Sticky headers pin to
    // the nearest scroll box, so a horizontal-only board would stop them pinning on vertical scroll.
    // The inner row takes its content's height so every column stretches to the tallest one and
    // its header stays pinned all the way down, not only for the board's visible height.
    // On a short screen, where wrapped filters leave little room, the board keeps a usable
    // minimum height and the page scrolls to reach it.
    <div className="mt-5 min-h-96 flex-1 overflow-auto pb-4" aria-label="Renewal board">
      <div className="flex min-h-full w-max gap-3">
        {BOARD_COLUMNS.map(({ bucket, hint, tone }) => {
          const { families: cards, totalValue } = group(bucket);
          return (
            <section
              key={bucket}
              className="flex w-64 shrink-0 flex-col rounded-lg bg-[#efe9e2]/60"
              aria-label={ACTION_BUCKET_LABELS[bucket]}
            >
              <header className={cn("sticky top-0 z-10 rounded-t-lg border px-3 py-2", tone)}>
                <div className="flex items-baseline justify-between">
                  <h2 className="text-sm font-semibold">{ACTION_BUCKET_LABELS[bucket]}</h2>
                  <span className="text-base font-bold tabular-nums">{cards.length}</span>
                </div>
                <div className="flex items-baseline justify-between text-[11px] opacity-80">
                  <span>{hint}</span>
                  <span className="tabular-nums">{totalValue}</span>
                </div>
              </header>
              <ul className="flex flex-col gap-2 p-2">
                {cards.map(family => (
                  <li key={family.relation_id}>
                    <RenewalCard family={family} ownerLabel={ownerLabel(family.owner_user_id)} />
                  </li>
                ))}
                {cards.length === 0 ? <li className="px-1 py-4 text-center text-xs text-gray-500">None</li> : null}
              </ul>
            </section>
          );
        })}
        <div className="sticky top-0 flex shrink-0 flex-col gap-3 self-start">
          {BOARD_TILES.map(bucket => {
            const { families: tiles, totalValue } = group(bucket);
            return (
              <section
                key={bucket}
                className="w-52 rounded-lg border border-[#e4ddd5] bg-[#fffdfb] p-4 text-center"
                aria-label={ACTION_BUCKET_LABELS[bucket]}
              >
                <h2 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                  {ACTION_BUCKET_LABELS[bucket]}
                </h2>
                <p className="mt-1 text-2xl font-bold tabular-nums text-[#111b35]">{tiles.length}</p>
                <p className="text-xs text-gray-500">contract families</p>
                <p className="mt-1 text-sm font-semibold text-[#111b35]">{totalValue} total value</p>
                <Button type="button" size="sm" className="mt-3" onClick={() => onViewInTable(bucket)}>
                  View in table →
                </Button>
              </section>
            );
          })}
        </div>
      </div>
    </div>
  );
};
